<template>
  <Menu
    v-if="availableLocales.length > 1"
    :items="items"
    :model-value="locale"
    align="end"
    @update:model-value="selectLocale"
  >
    <template #trigger>
      <Button
        :label="languageNameByLocale[locale] ?? locale"
        :right-icon-name="IconName.CHEVRON_DOWN"
        :variant="ButtonVariant.TEXT"
        :color="ButtonColor.SECONDARY"
      />
    </template>
  </Menu>
</template>

<script setup lang="ts">
import {
  Button,
  ButtonColor,
  ButtonVariant,
  IconName,
  Menu,
  type MenuEntry,
} from '@exfluential/ui';
import { useOrganizationStore } from '~/store/organization';

const { t, locale, locales, setLocale } = useI18n();

const { organization } = storeToRefs(useOrganizationStore());

const availableLocales = computed(() => {
  if (!organization.value) {
    return [];
  }

  return organization.value.locale.available;
});

const selectLocale = (value: string | undefined) => {
  const selectedLocale = availableLocales.value.find(
    (availableLocale) => availableLocale === value,
  );

  if (selectedLocale) {
    setLocale(selectedLocale);
  }
};

const languageNameByLocale = computed(() =>
  locales.value.reduce<Record<string, string>>((acc, locale) => {
    if (!locale.name) {
      throw new Error(
        `Language name is missing in i18n config for locale code: "locale.code"`,
      );
    }
    acc[locale.code] = locale.name;

    return acc;
  }, {}),
);

const items = computed<MenuEntry[]>(() => [
  { type: 'label', label: t('common.language') },
  { type: 'separator' },
  ...availableLocales.value.map((availableLocale) => ({
    label: languageNameByLocale.value[availableLocale] ?? availableLocale,
    value: availableLocale,
  })),
]);
</script>
